import {
  buildStrokeClusters,
  detectBhLeft,
  detectBhTopspinLeft,
  detectFhLeft,
  detectFhTopspinLeft,
  phasesOfStroke,
} from "./poseHeuristics";
import { getSequenceSubtypeLabel } from "./evaluationVideoMediaMeta";
import { resolveEffectiveStrokeClipKind } from "./strokeClipKind";

/** Match iOS StrokeMotionRangeDetector timing. */
export const PRE_ROLL_SECONDS = 1.0;
export const POST_ROLL_SECONDS = 1.0;
export const PRE_ROLL_RATE = 0.5;
export const STROKE_RATE = 0.2;
export const MAX_RALLY_PREP_RATE = 0.5;
export const MAX_RALLY_SWING_RATE = 0.2;
/** Surrounding rally (before/after the middle stroke) — was 1×, now half speed. */
export const MAX_RALLY_BASE_RATE = 0.5;
/** Still-frame hold at the last preparation frame, with knee overlay. */
export const KNEE_HOLD_SECONDS = 0.5;
/** Default when source fps is unknown. Prefer matching the source via resolveReelOutputFps. */
export const OUTPUT_FPS = 60;
export const OUTPUT_FPS_MAX = 120;
export const OUTPUT_WIDTH = 720;
export const OUTPUT_HEIGHT = 1280;

/** 0.5× (and 1×) segments keep source audio; 0.2× swing segments are muted. */
export function segmentReelAudioEnabled(segment) {
  const freeze = Math.max(0, Number(segment?.freezeSec) || 0);
  if (freeze > 0.05) return false;
  const scale = Number(segment?.timeScale) || 1;
  return scale < 5;
}

export function reelExportNeedsAudio(segments = []) {
  return (segments || []).some((seg) => segmentReelAudioEnabled(seg));
}

/** Snap a measured/hint fps to 30 / 60 / 120. Pose sample rates (<20) are ignored. */
export function snapSourceFps(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 20) return null;
  if (n >= 90) return 120;
  if (n >= 48) return 60;
  return 30;
}

/**
 * Export / encode fps: match source when known (up to 120) so 120fps clips
 * keep every frame instead of baking down to 60.
 */
export function resolveReelOutputFps(sourceFps) {
  const snapped = snapSourceFps(sourceFps);
  if (snapped) return Math.min(OUTPUT_FPS_MAX, snapped);
  return OUTPUT_FPS;
}

/**
 * Source-time gap between unique paints.
 * 120fps src + 120fps out at x1 → 1/120 (every frame).
 * 120fps at x0.5 → 1/120 (every source frame; each held 2× at 120 out).
 * 60fps at x1 → 1/60.
 */
export function sourceSamplePeriodSec(
  sourceFps,
  playbackRate,
  outputFps = OUTPUT_FPS
) {
  const src = Number(sourceFps) > 0 ? Number(sourceFps) : outputFps;
  const rate = Math.max(0.08, Number(playbackRate) || 1);
  const out = Number(outputFps) > 0 ? Number(outputFps) : OUTPUT_FPS;
  return Math.max(1 / src, rate / out);
}

export function sourceFrameBucket(mediaTime, samplePeriodSec) {
  const period = Number(samplePeriodSec);
  if (!(period > 0)) return 0;
  return Math.floor((Number(mediaTime) || 0) / period + 1e-6);
}

/**
 * One output frame per tick at `fps` (default 60). Freeze holds repeat the
 * same source time; motion maps evenly across the source span.
 */
export function buildOutputTimeline(segments, fps = OUTPUT_FPS) {
  const rate = Number(fps) > 0 ? Number(fps) : OUTPUT_FPS;
  const frames = [];
  for (const seg of segments || []) {
    const showKnees = Boolean(seg.showKnees);
    const sourceStart = Number(seg.sourceStart) || 0;
    const freeze = Math.max(0, Number(seg.freezeSec) || 0);
    const span = Math.max(0, Number(seg.sourceEnd) - sourceStart);
    const scale = Number(seg.timeScale) || 1;
    const freezeCount = Math.round(freeze * rate);
    for (let i = 0; i < freezeCount; i += 1) {
      frames.push({ sourceSec: sourceStart, showKnees });
    }
    const motionCount = Math.round(span * scale * rate);
    for (let i = 0; i < motionCount; i += 1) {
      const u = motionCount > 0 ? i / motionCount : 0;
      frames.push({ sourceSec: sourceStart + u * span, showKnees });
    }
  }
  return frames;
}

const DETECTION_PRIORITY = [
  "fh_topspin_left",
  "fh_left",
  "bh_topspin_left",
  "bh_left",
];

/** Warmup / sequence stroke kind → stored pose detection block. */
export const STROKE_KIND_TO_DETECTION_ID = {
  forehand: "fh_left",
  forehand_topspin: "fh_topspin_left",
  backhand: "bh_left",
  backhand_topspin: "bh_topspin_left",
};

const TOPSPIN_FAMILY_FALLBACK = {
  forehand_topspin: "fh_left",
  backhand_topspin: "bh_left",
};

/**
 * Reels always crop / overlay the person on the left of the frame.
 * Stroke heuristics (`fh_left`, `bh_left`, …) are computed on that side;
 * sequence “active” vs camera end must not switch the crop to player B.
 */
export function resolveActivePoseSide() {
  return "left";
}

function ownerRoleBlob(owner) {
  return `${owner?.roleKey || ""} ${owner?.role || ""}`.toLowerCase();
}

function ownerLooksActive(owner) {
  const r = ownerRoleBlob(owner);
  return (
    r.includes("active") ||
    r.includes("actif") ||
    r.includes("activo") ||
    r.includes("aktiv") ||
    r.includes("performance")
  );
}

function ownerLooksSparring(owner) {
  const r = ownerRoleBlob(owner);
  return r.includes("sparring") || r.includes("spar") || r.includes("partner");
}

/**
 * Highlighted name = active/performer (prefer left / side A).
 * Partner = sparring name for the discrete second line.
 */
/** When both players are active, prefer the selected player for reel overlay + HR. */
export function pickReelActivePlayerId(actives = [], selectedPlayerId = null) {
  const list = Array.isArray(actives) ? actives : [];
  const selected =
    selectedPlayerId != null ? String(selectedPlayerId).trim() : "";
  if (selected) {
    const match = list.find(
      (entry) => entry?.playerId != null && String(entry.playerId) === selected
    );
    if (match) return selected;
  }
  return list[0]?.playerId != null ? String(list[0].playerId) : selected || null;
}

/**
 * Warmup / sequence subtype as reel title (e.g. "Backhand Topspin"), with
 * detection-based fallback when labels are missing.
 */
export function warmupTitleForReel({
  clipKind = "",
  exerciseLabels = null,
  sequence = null,
  detectionId = "",
  localeCode = "en",
  fallbackTitle = "",
} = {}) {
  const subtype = getSequenceSubtypeLabel(sequence, localeCode);
  if (subtype) return subtype;
  const labels =
    exerciseLabels && typeof exerciseLabels === "object" ? exerciseLabels : {};
  if (localeCode === "fr" && labels.subtypeNameFr) {
    return String(labels.subtypeNameFr).trim();
  }
  if (labels.subtypeNameEn) return String(labels.subtypeNameEn).trim();
  if (labels.subtypeNameFr) return String(labels.subtypeNameFr).trim();
  if (fallbackTitle) return String(fallbackTitle).trim();
  const effectiveKind = resolveReelExerciseClipKind({
    clipKind,
    exerciseLabels,
    sequence,
  });
  if (effectiveKind === "backhand") return "Backhand";
  if (effectiveKind === "backhand_topspin") return "Topspin Backhand";
  if (effectiveKind === "forehand") return "Forehand";
  if (effectiveKind === "forehand_topspin") return "Topspin Forehand";
  if (detectionId === "bh_left") return "Backhand";
  if (detectionId === "bh_topspin_left") return "Topspin Backhand";
  if (detectionId === "fh_left") return "Forehand";
  if (detectionId === "fh_topspin_left") return "Topspin Forehand";
  return "";
}

export function pickReelOverlayPlayers(owners = [], { activePlayerId = null } = {}) {
  const list = (Array.isArray(owners) ? owners : []).filter(
    (o) => o && String(o.name || "").trim()
  );
  if (!list.length) return { performer: null, partner: null };

  const activeId = activePlayerId != null ? String(activePlayerId) : null;
  let performer =
    (activeId &&
      list.find((o) => o.playerId && String(o.playerId) === activeId)) ||
    list.find((o) => ownerLooksActive(o) && String(o.side || "").toUpperCase() === "A") ||
    list.find((o) => ownerLooksActive(o)) ||
    list.find((o) => String(o.side || "").toUpperCase() === "A") ||
    list[0] ||
    null;

  const partner =
    list.find((o) => o !== performer && ownerLooksSparring(o)) ||
    list.find((o) => o !== performer) ||
    null;

  return { performer, partner };
}

function detectionHasStrokes(det) {
  return Boolean(det && Array.isArray(det.strokes) && det.strokes.length);
}

/**
 * Prefer stored server detections; otherwise compute from pose samples.
 * Does not enqueue a new pose job.
 */
export function detectionsFromPoseResult(poseResult, viewSetup = null) {
  const server = poseResult?.summary?.detections || {};
  const fromServer = {
    fh_topspin_left: server.fh_topspin_left || null,
    fh_left: server.fh_left || null,
    bh_topspin_left: server.bh_topspin_left || null,
    bh_left: server.bh_left || null,
  };
  if (DETECTION_PRIORITY.some((key) => detectionHasStrokes(fromServer[key]))) {
    return fromServer;
  }
  const samples = poseResult?.samples;
  if (!Array.isArray(samples) || !samples.length) return fromServer;
  return {
    fh_topspin_left: detectFhTopspinLeft(samples, viewSetup),
    fh_left: detectFhLeft(samples, viewSetup),
    bh_topspin_left: detectBhTopspinLeft(samples, viewSetup),
    bh_left: detectBhLeft(samples, viewSetup),
  };
}

export function pickPrimaryStrokeDetection(detections = {}) {
  for (const key of DETECTION_PRIORITY) {
    const det = detections[key];
    if (det && Array.isArray(det.strokes) && det.strokes.length) {
      return { id: key, detection: det };
    }
  }
  return null;
}

/**
 * Pick the detection block that matches the warmup / sequence stroke type.
 * Falls back to plain FH/BH inside topspin warmups, then priority list.
 */
export function pickStrokeDetectionForClipKind(detections = {}, clipKind = "") {
  const kind = String(clipKind || "").trim();
  const primaryId = STROKE_KIND_TO_DETECTION_ID[kind];
  if (primaryId && detectionHasStrokes(detections[primaryId])) {
    return { id: primaryId, detection: detections[primaryId] };
  }
  const altId = TOPSPIN_FAMILY_FALLBACK[kind];
  if (altId && detectionHasStrokes(detections[altId])) {
    return { id: altId, detection: detections[altId] };
  }
  return null;
}

export function resolveReelExerciseClipKind({
  clipKind = "",
  exerciseLabels = null,
  sequence = null,
} = {}) {
  const labels = exerciseLabels && typeof exerciseLabels === "object"
    ? exerciseLabels
    : {};
  const seq = sequence && typeof sequence === "object" ? sequence : {};
  return resolveEffectiveStrokeClipKind({
    recipeKind: clipKind || labels.recipeKind || "",
    subtypeNameEn: labels.subtypeNameEn || seq.subtype_name_en || "",
    subtypeNameFr: labels.subtypeNameFr || seq.subtype_name_fr || "",
    sequenceLabels: labels.sequenceLabels || "",
    recipeKinds: labels.recipeKinds || null,
  });
}

function pickDetectionForExport(detections, opts = {}) {
  const effectiveKind = resolveReelExerciseClipKind(opts);
  if (effectiveKind) {
    return pickStrokeDetectionForClipKind(detections, effectiveKind);
  }
  return pickPrimaryStrokeDetection(detections);
}

/** Longest consecutive rally cluster (ties keep the first). */
export function pickMaxRallyCluster(strokes) {
  if (!Array.isArray(strokes) || !strokes.length) return [];
  const clusters = buildStrokeClusters(strokes);
  let bestCluster = clusters[0] || [];
  for (const cluster of clusters) {
    if ((cluster?.length || 0) > (bestCluster?.length || 0)) {
      bestCluster = cluster;
    }
  }
  return Array.isArray(bestCluster) ? bestCluster : [];
}

/** 0-based index of the middle stroke (3-stroke rally → 2nd). */
export function middleRallyStrokeIndex(count) {
  const n = Number(count) || 0;
  if (n <= 1) return 0;
  return Math.floor((n - 1) / 2);
}

function phaseMs(stroke, phaseKey, edge) {
  const phases = phasesOfStroke(stroke) || {};
  const range = phases[phaseKey];
  const t = Number(edge === "end" ? range?.t_end_ms : range?.t_start_ms);
  if (Number.isFinite(t)) return t;
  return null;
}

function firstStrokePrepStartMs(stroke) {
  return (
    phaseMs(stroke, "preparation", "start") ??
    Number(stroke?.t_valley_ms) ??
    Number(stroke?.t_peak_ms) ??
    0
  );
}

function lastStrokeRecoveryEndMs(stroke) {
  return (
    phaseMs(stroke, "recovery", "end") ??
    phaseMs(stroke, "follow_through", "end") ??
    Number(stroke?.t_peak_ms) ??
    0
  );
}

function middleStrokeForwardStartMs(stroke) {
  return (
    phaseMs(stroke, "forward_swing", "start") ??
    Number(stroke?.t_valley_ms) ??
    Number(stroke?.t_contact_ms) ??
    firstStrokePrepStartMs(stroke)
  );
}

function strokeRecoveryStartMs(stroke) {
  return (
    phaseMs(stroke, "recovery", "start") ??
    phaseMs(stroke, "follow_through", "end") ??
    lastStrokeRecoveryEndMs(stroke)
  );
}

function pushSegment(segments, sourceStart, sourceEnd, playbackRate, extra = {}) {
  const span = Number(sourceEnd) - Number(sourceStart);
  if (!(span > 0.01)) return;
  const rate = Math.max(0.08, Number(playbackRate) || 1);
  segments.push({
    sourceStart,
    sourceEnd,
    timeScale: 1 / rate,
    showKnees: Boolean(extra.showKnees),
  });
}

function pushHold(segments, sourceSec, holdSec, extra = {}) {
  const t = Number(sourceSec);
  const hold = Number(holdSec);
  if (!Number.isFinite(t) || !(hold > 0.05)) return;
  segments.push({
    sourceStart: t,
    sourceEnd: t,
    timeScale: 1,
    freezeSec: hold,
    showKnees: extra.showKnees !== false,
  });
}

/**
 * Clip the max rally: first prep → last recovery.
 * x0.5 until the middle stroke prep, x0.5 through that prep (no knees),
 * 0.5s still at last prep frame with knees, x0.2 from forward swing until
 * recovery (knees on), then hide knees and play x0.5 for the rest.
 */
export function buildMaxRallyClipSegments(cluster, videoDurationSec = Infinity) {
  const duration = Number.isFinite(videoDurationSec) ? videoDurationSec : Infinity;
  const toSec = (ms) =>
    Math.max(0, Math.min(duration, (Number(ms) || 0) / 1000));
  if (!Array.isArray(cluster) || !cluster.length) return [];

  const first = cluster[0];
  const last = cluster[cluster.length - 1];
  const mid = cluster[middleRallyStrokeIndex(cluster.length)] || first;
  const clipStart = toSec(firstStrokePrepStartMs(first));
  const clipEnd = Math.max(clipStart, toSec(lastStrokeRecoveryEndMs(last)));
  const prepStart = Math.max(clipStart, Math.min(toSec(firstStrokePrepStartMs(mid)), clipEnd));
  const forwardStart = Math.max(
    prepStart,
    Math.min(toSec(middleStrokeForwardStartMs(mid)), clipEnd)
  );
  let recoveryStart = Math.max(
    forwardStart,
    Math.min(toSec(strokeRecoveryStartMs(mid)), clipEnd)
  );
  if (recoveryStart <= forwardStart + 0.01) {
    recoveryStart = Math.max(
      forwardStart,
      Math.min(toSec(lastStrokeRecoveryEndMs(mid)), clipEnd)
    );
  }

  const segments = [];
  pushSegment(segments, clipStart, prepStart, MAX_RALLY_BASE_RATE);
  pushSegment(segments, prepStart, forwardStart, MAX_RALLY_PREP_RATE);
  pushHold(segments, forwardStart, KNEE_HOLD_SECONDS, { showKnees: true });
  pushSegment(segments, forwardStart, recoveryStart, MAX_RALLY_SWING_RATE, {
    showKnees: true,
  });
  pushSegment(segments, recoveryStart, clipEnd, MAX_RALLY_BASE_RATE);
  return segments;
}

/**
 * Longest rally cluster, then stroke with largest amplitude (iOS wrist-delta proxy).
 */
export function pickMaxRallyBestStroke(strokes) {
  const bestCluster = pickMaxRallyCluster(strokes);
  if (!bestCluster.length) return null;
  return bestCluster.reduce((best, stroke) => {
    const amp = Number(stroke?.amplitude_ms) || 0;
    const bestAmp = Number(best?.amplitude_ms) || 0;
    return amp >= bestAmp ? stroke : best;
  }, bestCluster[0]);
}

export function buildStrokeMotionRange(stroke, videoDurationSec = Infinity) {
  const lowestMs =
    Number(stroke?.t_valley_ms) ||
    Number(stroke?.phases?.forward_swing?.t_start_ms) ||
    Number(stroke?.t_peak_ms) ||
    0;
  const highestMs =
    Number(stroke?.phases?.follow_through?.t_end_ms) ||
    Number(stroke?.t_peak_ms) ||
    lowestMs;
  const lowest = lowestMs / 1000;
  const highest = Math.max(lowest, highestMs / 1000);
  const recoveryStartMs =
    phaseMs(stroke, "recovery", "start") ??
    phaseMs(stroke, "follow_through", "end") ??
    highestMs;
  const preRollStart = Math.max(0, lowest - PRE_ROLL_SECONDS);
  const clipEnd = Math.min(
    Number.isFinite(videoDurationSec) ? videoDurationSec : Infinity,
    highest + POST_ROLL_SECONDS
  );
  const recoveryStart = Math.max(lowest, Math.min(recoveryStartMs / 1000, clipEnd));
  return { lowest, highest, preRollStart, clipEnd, recoveryStart };
}

/**
 * @returns {Array<{ sourceStart: number, sourceEnd: number, timeScale: number, showKnees?: boolean, freezeSec?: number }>}
 */
export function buildClipSegments(range, videoDurationSec = Infinity) {
  const duration = Number.isFinite(videoDurationSec) ? videoDurationSec : Infinity;
  const segments = [];
  const preRollStart = Math.max(0, Math.min(range.preRollStart, duration));
  const lowest = Math.max(preRollStart, Math.min(range.lowest, duration));
  const highest = Math.max(lowest, Math.min(range.highest, duration));
  const postRollEnd = Math.min(duration, highest + POST_ROLL_SECONDS);
  let recoveryStart = Math.max(
    lowest,
    Math.min(Number(range.recoveryStart) || highest, postRollEnd)
  );
  if (recoveryStart <= lowest + 0.01) recoveryStart = highest;

  pushSegment(segments, preRollStart, lowest, PRE_ROLL_RATE);
  pushHold(segments, lowest, KNEE_HOLD_SECONDS, { showKnees: true });
  pushSegment(segments, lowest, recoveryStart, STROKE_RATE, { showKnees: true });
  pushSegment(segments, recoveryStart, postRollEnd, 1);
  return segments;
}

export function outputDurationSeconds(segments) {
  let total = 0;
  for (const seg of segments || []) {
    const freeze = Math.max(0, Number(seg.freezeSec) || 0);
    const span = Math.max(0, Number(seg.sourceEnd) - Number(seg.sourceStart));
    total += freeze + span * (Number(seg.timeScale) || 1);
  }
  return total;
}

/** Normalize stroke count to a full minute using eval clip duration. */
export function computeStrokesPerMinute(strokeCount, videoDurationSec) {
  const count = Number(strokeCount);
  const duration = Number(videoDurationSec);
  if (!Number.isFinite(count) || count < 0) return null;
  if (!Number.isFinite(duration) || duration <= 0) return null;
  return Math.round((count / duration) * 60);
}

export function countPrimaryDetectionStrokes(detections = {}) {
  const picked = pickPrimaryStrokeDetection(detections);
  return picked?.detection?.strokes?.length ?? 0;
}

/** Map exported timeline seconds back to source video seconds. */
export function mapOutputTimeToSource(outputSec, segments) {
  let cursor = 0;
  for (const seg of segments || []) {
    const freeze = Math.max(0, Number(seg.freezeSec) || 0);
    if (freeze > 0 && outputSec <= cursor + freeze + 1e-6) {
      return Number(seg.sourceStart);
    }
    cursor += freeze;
    const span = Math.max(0, Number(seg.sourceEnd) - Number(seg.sourceStart));
    const outSpan = span * (Number(seg.timeScale) || 1);
    if (outputSec <= cursor + outSpan + 1e-6) {
      const alpha = outSpan > 0 ? (outputSec - cursor) / outSpan : 0;
      return Number(seg.sourceStart) + alpha * span;
    }
    cursor += outSpan;
  }
  const last = segments?.[segments.length - 1];
  return last ? Number(last.sourceEnd) : 0;
}

export function planStrokeSlowMoExport({
  detections,
  videoDurationSec,
  clipKind = "",
  exerciseLabels = null,
  sequence = null,
} = {}) {
  const picked = pickDetectionForExport(detections, {
    clipKind,
    exerciseLabels,
    sequence,
  });
  if (!picked) {
    return { ok: false, reason: "no_strokes" };
  }
  const stroke = pickMaxRallyBestStroke(picked.detection.strokes);
  if (!stroke) {
    return { ok: false, reason: "no_strokes" };
  }
  const range = buildStrokeMotionRange(stroke, videoDurationSec);
  const segments = buildClipSegments(range, videoDurationSec);
  if (!segments.length) {
    return { ok: false, reason: "no_segments" };
  }
  const strokeCount = picked.detection.strokes.length;
  const strokesPerMinute = computeStrokesPerMinute(strokeCount, videoDurationSec);
  return {
    ok: true,
    detectionId: picked.id,
    stroke,
    strokeCount,
    strokesPerMinute,
    range,
    segments,
    outputDurationSec: outputDurationSeconds(segments),
    poseSide: resolveActivePoseSide(),
  };
}

export function planMaxRallyExport({
  detections,
  videoDurationSec,
  clipKind = "",
  exerciseLabels = null,
  sequence = null,
} = {}) {
  const picked = pickDetectionForExport(detections, {
    clipKind,
    exerciseLabels,
    sequence,
  });
  if (!picked) {
    return { ok: false, reason: "no_strokes" };
  }
  const cluster = pickMaxRallyCluster(picked.detection.strokes);
  if (!cluster.length) {
    return { ok: false, reason: "no_strokes" };
  }
  const segments = buildMaxRallyClipSegments(cluster, videoDurationSec);
  if (!segments.length) {
    return { ok: false, reason: "no_segments" };
  }
  const strokeCount = picked.detection.strokes.length;
  const rallyStrokeCount = cluster.length;
  return {
    ok: true,
    kind: "max_rally",
    detectionId: picked.id,
    cluster,
    rallyStrokeCount,
    strokeCount,
    strokesPerMinute: computeStrokesPerMinute(strokeCount, videoDurationSec),
    segments,
    outputDurationSec: outputDurationSeconds(segments),
    poseSide: resolveActivePoseSide(),
  };
}
